import { useEffect, useState } from 'react'

declare global {
  interface Window {
    Clerk?: {
      user?: unknown
      session?: {
        getToken: (opts?: { skipCache?: boolean }) => Promise<string | null>
      } | null
      mountSignIn: (el: HTMLElement, opts?: Record<string, unknown>) => void
      mountSignUp: (el: HTMLElement, opts?: Record<string, unknown>) => void
      unmountSignIn: (el: HTMLElement) => void
      unmountSignUp: (el: HTMLElement) => void
      signOut?: () => Promise<void>
    }
    onClerkReady?: () => void
  }
}

let clerkLoadPromise: Promise<void> | null = null

/**
 * Idempotently load clerk-js and resolve once it's ready. Exported so the API
 * token getter (main.tsx) can await readiness before minting a session token —
 * otherwise the first patient request fires before clerk-js exists, mints no
 * token, and 401s (the transient dashboard/profile 401s seen on cold load).
 */
export function loadClerk(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.Clerk?.user !== undefined) return Promise.resolve()
  if (clerkLoadPromise) return clerkLoadPromise

  clerkLoadPromise = new Promise<void>((resolve) => {
    window.onClerkReady = () => resolve()
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-pelvia-clerk-init="1"]',
    )
    if (existing) return
    const s = document.createElement('script')
    s.src = '/api/clerk-init.js'
    s.dataset.pelviaClerkInit = '1'
    document.head.appendChild(s)
  })

  return clerkLoadPromise
}

export function useClerk() {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let cancelled = false
    loadClerk().then(() => {
      if (!cancelled) setReady(true)
    })
    return () => {
      cancelled = true
    }
  }, [])

  return { ready, clerk: ready ? window.Clerk : undefined }
}
